import React from 'react';
import Show from '../Show/Show';
import { H2 } from '../../config/designSystem/typography';
import { Ionicons } from '@expo/vector-icons';
import { TouchableOpacity, View } from 'react-native';
import { VideoView, useVideoPlayer } from 'expo-video';
import { setAudioModeAsync } from 'expo-audio';
import { useEvent } from 'expo';
import { captureException } from '../../utils/sentry/sentry';

interface VideoProps {
  source: string;
  widthVideo: number;
  heightVideo?: number;
  paddingVideo?: number;
  title: string;
  color: string;
  setPositionMillis: (position: number) => void;
  setPositionPercent: (percent: number) => void;
}

/**
 * Migrated from expo-av's <Video> (removed in SDK 55) to expo-video's <VideoView>.
 *
 * expo-video separates the player object from the view, so imperative control and
 * playback progress come from the player rather than component props/refs.
 * UNITS: expo-av reported milliseconds; expo-video reports SECONDS. The
 * setPositionMillis/setPositionPercent props still expect milliseconds.
 */
const VideoComponent = ({ paddingVideo, source, title, widthVideo, heightVideo, color, setPositionMillis, setPositionPercent }: VideoProps) => {
  const PADDING_VIDEO = paddingVideo?paddingVideo:20;
  const DEFAULT_RATIO = 16/9;
  const [ratio, setRatio] = React.useState<number>(DEFAULT_RATIO);
  const [withNativeControls, setWithNativeControls] = React.useState(false);
  const [widthDynamic, setWidthDynamic] = React.useState<number>((widthVideo-2*PADDING_VIDEO));
  const [heightDynamic, setHeightDynamic] = React.useState<number>(heightVideo?heightVideo:DEFAULT_RATIO/widthVideo);

  const player = useVideoPlayer({ uri: source }, p => {
    // how often the `timeUpdate` event fires, in seconds
    p.timeUpdateEventInterval = 0.5;
  });

  // replaces expo-av's onPlaybackStatusUpdate
  const timeUpdate = useEvent(player, 'timeUpdate');
  // replaces expo-av's onReadyForDisplay / onLoad (naturalSize)
  const sourceLoad = useEvent(player, 'sourceLoad');

  React.useEffect(() => {
    // NOTE: previously this ran unconditionally in the render body, firing on every
    // re-render. It belongs in an effect.
    setAudioModeAsync({
      allowsRecording: false,
      playsInSilentMode: true,
      interruptionMode: 'duckOthers',
      interruptionModeAndroid: 'duckOthers',
      shouldPlayInBackground: true,
      shouldRouteThroughEarpiece: false,
    }).catch(captureException);
  }, []);

  React.useEffect(() => {
    const size = sourceLoad?.availableVideoTracks?.[0]?.size;
    if (size?.width && size?.height) {
      setRatio(size.width / size.height);
    }
  }, [sourceLoad]);

  React.useEffect(() => {
    try {
      const currentTime = timeUpdate?.currentTime;
      if (typeof currentTime !== 'number') return;
      setPositionMillis && setPositionMillis(currentTime * 1000);
      const duration = player.duration;
      if (duration) {
        setPositionPercent && setPositionPercent(currentTime / duration);
      }
    } catch (error) {
      captureException(error);
    }
  }, [timeUpdate]);

  React.useEffect(() => {
    if(heightVideo && ratio*heightVideo<(widthVideo-2*PADDING_VIDEO)) {
      setWidthDynamic(ratio*heightVideo);
      setHeightDynamic(heightVideo);
    } else {
      setWidthDynamic((widthVideo-2*PADDING_VIDEO));
      setHeightDynamic((widthVideo-2*PADDING_VIDEO)/ratio);
    }
  }, [heightVideo,widthVideo, ratio]);

  const ICON_SIZE = 60;

  return (
    <View style={{ width: widthDynamic+2*PADDING_VIDEO,  paddingHorizontal: PADDING_VIDEO}}>
      <Show when={title && title.length>0}>
        <View style={{alignItems: 'center', justifyContent:'center', marginBottom:20}}>
          <H2>
            {title}
          </H2>
        </View>
      </Show>
      <View style={{height:heightDynamic}}>
        <VideoView
          player={player}
          contentFit='contain'
          nativeControls={withNativeControls}
          style={{
            height: '100%',
            width: '100%',
            borderRadius:10
          }}
        />
        { !withNativeControls && <View style={{position:'absolute', left: (widthDynamic-ICON_SIZE)/2, top: (heightDynamic-ICON_SIZE)/2}}>

          <TouchableOpacity onPress={() => {
            player.play();
            setWithNativeControls(true);
          }}>
            <View style={{
              height: ICON_SIZE,
              width: ICON_SIZE,
              paddingLeft: 5,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor:color,
              borderRadius: 40}}>
              <Ionicons name='play' size={ICON_SIZE*0.7} color='#fff' />
            </View>
          </TouchableOpacity>
        </View>
        }
      </View>
    </View>);
};


export default VideoComponent;
